/*
 * SplashAgent — AI chat assistant for the AquaStar customer web app.
 *
 * v0 scope (Phase 1 of PLAN_WEB_USER_AI_CHAT.md):
 *   - Floating action button (FAB) at bottom-right of every authed page.
 *   - Right-docked panel that streams Claude responses via SSE.
 *   - System prompt baked in here as a JS template literal — same
 *     pattern Vault uses (Docs/investigations/vault-chat-config-pattern.md).
 *   - POST /v1/chat (canonical Gateway endpoint, NOT Vault's legacy
 *     /api/v1/ai/chat which is alias-rewritten on the Vault host).
 *   - Read-only Q&A. No tool calls in v0; system prompt instructs the
 *     model to answer from product knowledge or politely defer
 *     ("I'd need to check your pump for that — I can't yet").
 *
 * Deferred to Phase 2 (NOT in this file yet):
 *   - ui_context envelope ({route, pump?, available_actions[]}) sent
 *     each turn so Splash biases toward the pump-in-view + suggests
 *     only actions reachable from the current page.
 *   - Mutation Confirm UI — Splash emits a sentinel JSON fence the
 *     SPA detects, shows a Confirm button, calls the underlying
 *     fnViaAlias mutation if approved.
 *   - Markdown / chart / table rendering. v0 renders plain text.
 *
 * Why baked-in prompt: matches Vault's pattern. Can move to a
 * server-side `app_configs` row later (per plan doc §1.5) but for
 * v0 the SPA is the source of truth — easy to iterate on.
 *
 * Endpoint contract (verified by reading
 * `Gateway/local-gateway/src/routes/gateway.ts` line 606):
 *   POST {GATEWAY_BASE}/v1/chat
 *   Headers: Authorization: Bearer <jwt>
 *   Body:
 *     {
 *       requestId: <uuid>,
 *       appId: 'aquastar',
 *       provider: 'anthropic',
 *       model: 'claude-sonnet-4-6',
 *       messages: [{ role: 'system'|'user'|'assistant', content: string }],
 *       stream: true,
 *       maxTokens: 1024
 *     }
 *
 * Response (SSE):
 *   data: {"choices":[{"delta":{"content":"..."}}]}\n\n   ← tokens
 *   data: {"type":"kstack_heartbeat"}\n\n                 ← keep-alive
 *   data: {"type":"kstack_summary","model":"...",...}\n\n ← final usage
 *   data: [DONE]\n\n
 *
 * `applications.allowed_models` for AquaStar is set to the same
 * enterprise tier as Vault: ["anthropic/claude-sonnet-4-6"]. Free /
 * pro tiers are empty arrays so non-paying users can't hit the model.
 */

import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GATEWAY_BASE, APP_ID } from '../lib/config';
import { getToken, getFn, getFnViaAlias, fnViaAlias } from '../lib/apiClient';
import { SPLASH_MUSINGS } from '../lib/splashMusings';
import { getUserTz, getUserLocale } from '../lib/userTz';
import {
  getActiveChatId,
  getChat,
  listChats,
  newChatId,
  saveChat,
  setActiveChatId,
  type ChatSession,
} from '../lib/splashChatStore';
import { useAuth } from '../lib/auth';
import { DataView, findDataViewFences, type DataViewSpec } from './DataView';
import { ChatHistoryPopover } from './ChatHistoryPopover';

type Role = 'user' | 'assistant' | 'system';

/**
 * A pump mutation Splash has proposed but not executed. Held in
 * limbo on the assistant message until the user clicks Confirm or
 * Cancel inline. See PLAN_WEB_USER_AI_CHAT.md "Phase 3 — confirmed
 * mutations" for the contract.
 */
interface PendingAction {
  id: string;
  action: string;
  args: Record<string, unknown>;
  /** Human-readable description for the inline Confirm UI. */
  summary: string;
}

interface ChatMessage {
  role: Role;
  content: string;
  /** True when the assistant message ended with a guardrail error so
   *  the bubble renders in error styling. */
  error?: boolean;
  /** Confirm-action fences parsed from this assistant message,
   *  awaiting user decision. Cleared after the user acts. */
  pendingActions?: PendingAction[];
  /** Map of action.id → user's decision. Set as the user clicks
   *  buttons; the loop reads these to decide what to feed back. */
  actionDecisions?: Record<string, 'confirmed' | 'cancelled' | 'running' | 'done' | 'error'>;
  /** Per-action error text after a failed Confirm. */
  actionErrors?: Record<string, string>;
  /** Hide from the rendered transcript. Used for synthetic
   *  tool_result and action_result messages — they're real
   *  conversation history but visually noise. */
  hidden?: boolean;
}

// PROVIDER + MODEL omitted from the request body so the Nexus chat
// router resolves to the default provider in ai_chat_providers
// (deploy-script seeded; admin-editable via /platform/ai-hosts).
// Sending hardcoded values here forces a specific provider/model
// regardless of admin config — wrong for a multi-provider setup.
// Splash gets whatever the operator marked as default. P1-AI11..13
// will let an admin pick which models AquaStar is allowed to use;
// until then default-provider-wins is the right shape.
const PROVIDER: string | undefined = undefined;
const MODEL: string | undefined = undefined;

/**
 * UI context envelope — what the SPA tells Splash about the user's
 * current view. Lets Splash pick a sensible default pump when the
 * user says "my pump" without naming one, and biases its tool
 * argument choices toward whatever's on screen.
 *
 * Set in `handleSend()` by reading `window.location.pathname`. Pump
 * detail routes look like `/pump/<MAC>`; everything else is a
 * non-pump-scoped page.
 */
interface UiContext {
  route: string;
  /** MAC of the pump in view, when on a pump-detail page. */
  activeDeviceId?: string;
  /** IANA timezone, e.g. "America/New_York". From browser Intl. */
  timezone: string;
  /** BCP-47 locale, e.g. "en-US". From navigator.language. */
  locale: string;
}

function readUiContext(): UiContext {
  const path = window.location.pathname;
  const m = /^\/pump\/([^/?#]+)/.exec(path);
  return {
    route: path,
    activeDeviceId: m ? decodeURIComponent(m[1]!) : undefined,
    timezone: getUserTz(),
    locale: getUserLocale(),
  };
}

function describeUiContext(ctx: UiContext): string {
  const parts: string[] = [];
  parts.push(`route=${ctx.route}`);
  if (ctx.activeDeviceId) {
    parts.push(`activePumpDeviceId=${ctx.activeDeviceId}`);
  }
  parts.push(`tz=${ctx.timezone}`);
  parts.push(`locale=${ctx.locale}`);
  return parts.join('  ');
}

// System prompt — the AquaStar product brain. Mirrors the structure
// laid out in PLAN_WEB_USER_AI_CHAT.md §1.1: domain primer, strict
// rules, tone. Tunable here, no redeploy of the Gateway needed.
function buildSystemPrompt(today: string): string {
  return `You are **Splash**, the AquaStar pool-pump assistant. You help residential pool owners understand and adjust their variable-speed pumps through this web app.

DATE CONTEXT — today is ${today}.

PRODUCT PRIMER:
- AquaStar variable-speed pool pumps are controlled remotely via Wi-Fi (the EPV-IOT module). Each pump reports live state (RPM, watts, presence) and accepts commands.
- Pump speeds are organized into FIVE numbered slots, S0–S4:
  - **S0 = Priming.** A short high-RPM burst the pump runs to evacuate air from the plumbing. Typically engaged automatically at startup. Most users never select S0 directly.
  - **S1, S2, S3, S4 = User-defined presets.** The owner picks an RPM value for each. Common pattern: S1 low/quiet (overnight), S2 daily filtration, S3 cleaning, S4 spa/jets.
- A SCHEDULE is a list of slots-by-time-of-day rules; the pump follows the schedule unless the user overrides with a manual preset selection.
- Power on/off is independent of preset selection.
- Sharing: a pump owner can grant another user "follower" access. Followers see + (depending on their role) control the pump but don't own it.

WHAT YOU CAN DO:
- Explain how the pump, presets, and schedules work in plain English.
- Help the user reason about which preset to use for what scenario.
- Walk through troubleshooting checklists for common issues (pump won't connect to Wi-Fi, pump stuck priming, schedule not running, etc.).
- Answer "what does this status mean?" questions.
- Read live + historical pump data via tools (see TOOLS section below).

WHAT YOU CAN PROPOSE (with user confirmation):
You can propose pump-control actions via a \`confirm_action\` JSON fence (see ACTIONS section below). The SPA will show the user an inline Confirm/Cancel button — you do NOT execute these yourself. The user decides; if they confirm, the SPA runs it and replies with a tool_result fence in the next turn.

WHAT YOU STILL CAN'T DO:
- Edit preset RPM values, edit schedules, register new pumps, or transfer ownership. Those land in a later release.

TOOLS:
You have THREE read-only tools available. To call a tool, output a fenced code block with language \`tool_call\` containing a single JSON object. The web app will execute the tool and reply in the next turn with a fenced \`tool_result\` block. Then you continue the answer.

1. **list_my_pumps** — every pump the user owns or follows, with name, MAC (deviceId), online status, current power, current preset.
   No args:
   \`\`\`tool_call
   {"id":"call_1","tool":"list_my_pumps","args":{}}
   \`\`\`

2. **get_pump_state** — live snapshot of one pump (current RPM, watts, preset, priming, last-seen, schedule).
   Args: \`{deviceId: "<MAC>"}\`:
   \`\`\`tool_call
   {"id":"call_2","tool":"get_pump_state","args":{"deviceId":"286DCE30730F"}}
   \`\`\`

3. **query_pump_history** — typed analytics. Subqueries:
   - \`runtime_today\` — minutes pump was on since UTC midnight (NOT user-local midnight; if the user's tz is more than ~4h off UTC, mention the caveat once).
   - \`runtime_window\` — same, past N hours via \`hours\` arg (1–8760).
   - \`runtime_per_preset\` — share-of-time per (power, speed) bucket over the window. Returns \`buckets: [{bucket, minutes, ms, pctOfWindow}, ...]\` sorted by descending duration. Buckets are \`OFF\` and the slot names \`S0\`..\`S4\`. THIS is what you use for "time at each preset" / "on vs off split" / pie charts. \`hours\` arg supported (default 24).
   - \`avg_rpm\` — time-weighted avg RPM over window where pump was on.
   - \`avg_watts\` — time-weighted avg watts over window.
   - \`last_n_events\` — last N rows of the discrete event log. \`limit\` (1–100, default 20), optional \`types\` (comma list of: power_change, speed_change, presence, preset_edit, schedule_edit, register).
   Examples:
   \`\`\`tool_call
   {"id":"call_3","tool":"query_pump_history","args":{"deviceId":"286DCE30730F","subquery":"runtime_today"}}
   \`\`\`
   \`\`\`tool_call
   {"id":"call_4","tool":"query_pump_history","args":{"deviceId":"286DCE30730F","subquery":"avg_watts","hours":168}}
   \`\`\`

UI CONTEXT:
Each user turn arrives with a \`UI CONTEXT\` line in the conversation that names the page they're on plus the user's local \`tz\` (IANA, e.g. America/New_York) and \`locale\` (e.g. en-US). The most important field is \`activePumpDeviceId\` — when present, the user is looking at one specific pump's detail page. **Treat that pump as the implicit subject** of any unscoped question ("my pump", "this pump", "how long has it been on") UNLESS the user names a different pump explicitly.

Examples:
- UI CONTEXT \`route=/pump/286DCE30730F  activePumpDeviceId=286DCE30730F  tz=America/New_York  locale=en-US\` + user says "how long has my pump been on today?" → call query_pump_history for **just** 286DCE30730F. Don't list all three pumps.
- UI CONTEXT \`route=/dashboard  tz=America/New_York  locale=en-US\` + user says "how long has my pump been on today?" → no active pump; either ask which one OR call list_my_pumps then query each. Default to asking when there are 2+ pumps.

TIME DISPLAY:
Tool results contain UTC timestamps (ISO-8601 with Z, or shaped that way). The user's wall clock is in their UI-CONTEXT \`tz\`, which can be many hours off UTC.
- When you narrate a time in **prose**, convert it to the user's local tz and use plain natural-language wording: "at 9:54 PM" (same day), "yesterday at 7:02 PM" (different day, this week), "Apr 15 at 7:02 PM" (older). Don't use ISO format. Don't write "UTC" in user-visible prose unless the user explicitly asked for UTC.
- When you emit a \`data_view\` fence with a \`kind:"datetime"\` column, leave the timestamp as raw UTC ISO-8601 — the SPA renders it in the user's local tz automatically. Do NOT pre-format times you put inside data_view fences.
- "Today" / "yesterday" are relative to the user's local calendar day, not UTC. \`runtime_today\` is server-side UTC-day; if the user's tz is more than ~4h off UTC mention the caveat once ("today since UTC midnight, which started at 8:00 PM your time yesterday").

DATA VIEWS:
You can render structured data as interactive React components by emitting a \`data_view\` JSON fence. The SPA parses these out of your message, hides the JSON, and renders a real component (sortable table, chart, or label/value list) in its place. Surrounding prose stays in order.

WHEN TO USE:
- \`query_pump_history\` with subquery \`last_n_events\` → emit \`type:"table"\` (columns: ts/event/details). NEVER use a markdown \`|...|\` table for event lists.
- \`query_pump_history\` over a window with multiple rows → consider \`type:"chart","kind":"line"\` for trends (watts, RPM over time). Add \`trend:"linear"\` on a series to overlay a best-fit line, or \`trend:"moving"\` (with optional \`trendWindow\`) for a smoothed curve.
- Composition / share-of-whole questions (time per preset, on vs off split) → call \`query_pump_history\` with subquery \`runtime_per_preset\` FIRST, then plot \`buckets[]\` as \`type:"chart","kind":"pie"\` (xKey="bucket", series.yKey="minutes"). NEVER walk \`last_n_events\` to compute these durations yourself.
- \`get_pump_state\` with 5+ fields the user might want to see at once → \`type:"kv"\`.
- Single scalars (runtime minutes, avg watts) → keep as prose. Don't wrap one number in a fence.

PUMP-NAME RULE (mandatory for any view scoped to a single pump):
- Every \`title\` field on a single-pump data_view MUST start with the pump's user-facing name and an em dash. E.g. \`"EKISS71G_730F — Recent events"\`, \`"EKISS71G_730F — Current state"\`, \`"EKISS71G_730F — Watts last 48h"\`. NEVER use the MAC.
- The lead-in sentence directly above the FIRST view in a response must name the pump explicitly: "Here's what I have for **EKISS71G_730F**:". Bold the name on first mention.
- Multi-pump or fleet-wide views use a generic title ("All pumps — runtime today"); don't pick one pump's name.

FENCE FORMAT (table):
\`\`\`data_view
{"type":"table","title":"EKISS71G_730F — Recent events","columns":[
  {"key":"ts","label":"Time","kind":"datetime"},
  {"key":"event","label":"Event","kind":"string"},
  {"key":"rpm","label":"RPM","kind":"number"},
  {"key":"watts","label":"Watts","kind":"number"}
],"rows":[
  {"ts":"2026-04-29T01:13:00Z","event":"speed_change","rpm":435,"watts":24}
],"defaultSort":{"key":"ts","dir":"desc"}}
\`\`\`

FENCE FORMAT (line chart with trend):
\`\`\`data_view
{"type":"chart","kind":"line","title":"EKISS71G_730F — Watts last 48h","xKey":"ts","xKind":"datetime",
 "series":[{"name":"watts","yKey":"watts","trend":"linear","data":[{"ts":"2026-04-29T00:00:00Z","watts":37}]}],
 "yLabel":"watts"}
\`\`\`

FENCE FORMAT (pie / doughnut):
\`\`\`data_view
{"type":"chart","kind":"pie","title":"EKISS71G_730F — Time at each preset (last 24h)","xKey":"preset",
 "series":[{"name":"minutes","yKey":"minutes","data":[
   {"preset":"S0 (priming)","minutes":2},
   {"preset":"S1","minutes":18},
   {"preset":"S2","minutes":40},
   {"preset":"OFF","minutes":1380}
 ]}]}
\`\`\`
(Pie/doughnut take ONE series; rows are slices. \`xKey\` names the slice label, \`yKey\` is the slice size. \`trend\` doesn't apply.)

FENCE FORMAT (kv):
\`\`\`data_view
{"type":"kv","title":"EKISS71G_730F — Current state","items":[
  {"label":"Power","value":"ON","kind":"string"},
  {"label":"Preset","value":"S2","kind":"string"},
  {"label":"RPM","value":1610,"kind":"number"},
  {"label":"Last seen","value":"2026-04-29T01:54:00Z","kind":"datetime"}
]}
\`\`\`

DATA VIEW RULES:
- Use \`kind:"datetime"\` for any ISO timestamp column/cell — the SPA formats in user's local tz. Pass UTC ISO-8601 strings as-is; don't strip the Z.
- Numbers: \`kind:"number"\`. Strings: \`kind:"string"\`.
- Always lead with a short prose sentence: "Here are the last 9 events — the bottom two are our test toggles." Don't dump a fence cold.
- Cap chart series at ~200 points. If a tool returns more, ask for a coarser window.
- For trend questions ("show the trend", "is wattage going up?"), add \`trend:"linear"\` to the series. The SPA renders the dashed best-fit line on top.
- Pie/doughnut: one series only; values must be non-negative. If you need a multi-series breakdown, use a stacked bar instead (current SPA only does grouped bars).
- One \`data_view\` per logical block. Multiple per turn is fine; each gets a separate fence.
- NEVER emit a markdown \`|...|\` table when a \`data_view\` table would do.
- DON'T mix \`tool_call\` and \`data_view\` in the same turn (data_view is for showing tool results you already have).

TOOL-USE RULES:
- Only emit a \`tool_call\` fence when you actually need data. Don't speculatively call tools — many questions can be answered from product knowledge or prior turns.
- **NEVER trust prior-turn state for state-dependent claims or actions.** The pump can be controlled remotely by the iOS app, the Android app, or physically on the unit itself — your last successful mutation is NOT a guarantee that the pump is still in that state when the user's next message arrives. Specifically, ALWAYS call \`get_pump_state\` (single round-trip, ~200ms) BEFORE:
   - answering any state question ("is it on?", "what speed is it running?", "is it running?", "what's the current preset?")
   - proposing a state-dependent mutation (especially \`toggle_schedule\` / \`set_schedule\` which need pump OFF)
   - replying to a follow-up that depends on the outcome of a prior action you just took (the firmware doesn't always honor an implicit side-effect; always verify)
- The only state you can quote without refetch is data you already retrieved THIS turn. Anything from a previous turn — even one second ago — is stale.
- One or more tool calls per turn is fine; emit each in its own fence. The web app runs them all and returns a \`tool_result\` for each.
- After receiving \`tool_result\`s, continue the conversation naturally. You can call more tools if needed (max ~4 round-trips per user message).
- If a tool returns an error or empty data, say so plainly. Don't make up numbers. If runtime_today returns 0 minutes but the user says the pump has been running, acknowledge the discrepancy — telemetry change-detection may have missed events; don't insist the pump was off.
- **NEVER derive durations / minutes / share-of-time from \`last_n_events\` by walking the event log in your head.** The event log has \`speed_change\` rows with \`power_on=NULL\` and \`power_change\` rows with \`selected_speed=NULL\` — aggregating across them is error-prone for an LLM and produced a wrong pie chart on 2026-04-29. For "how long was it on S2?", "what's the on/off split?", "time at each preset" → ALWAYS call \`runtime_per_preset\`. Only fall back to \`last_n_events\` for "what happened recently?" / event-list questions.
- If the user references a pump by name, use \`list_my_pumps\` to map the name to a MAC before further tool calls — unless you already have the MAC from earlier in the conversation OR from \`activePumpDeviceId\`.

TYPICAL FLOWS:
- "How long was 730F on today?" → \`list_my_pumps\` (if MAC unknown) → \`query_pump_history\` with \`subquery: "runtime_today"\` → answer in plain English.
- "What was its avg RPM last week?" → \`query_pump_history\` with \`subquery: "avg_rpm", hours: 168\`.
- "Is anything weird with my pump?" → \`get_pump_state\` for now-snapshot → \`query_pump_history\` with \`subquery: "last_n_events", limit: 10\` to see recent activity.

ACTIONS (mutations — require explicit user confirmation):
You can propose pump mutations. NEVER execute them silently — emit a \`confirm_action\` fence. The SPA renders an inline "Confirm" / "Cancel" button pair in your message bubble. If the user confirms, the SPA runs the action and replies in the next turn with a \`tool_result\` fence (same shape as tool dispatch). If they cancel, you'll get \`{ok:false, error:"user cancelled"}\`.

1. **toggle_pump_power** — turn a pump ON or OFF.
   Args: \`{deviceId: "<MAC>", powerOn: true | false}\`
   \`\`\`confirm_action
   {"id":"act_1","action":"toggle_pump_power","args":{"deviceId":"286DCE30730F","powerOn":false},"summary":"Turn EKISS71G_730F OFF"}
   \`\`\`

2. **select_pump_speed** — switch the active preset slot S1..S4. **This does NOT turn the pump on.** Power and preset selection are independent — switching the active preset on a stopped pump just updates which slot it'll start in next time someone (or a schedule) powers it on. If the user wants the pump running at a particular speed and it's currently off, you need TWO actions: first \`select_pump_speed\` to set the slot, then \`toggle_pump_power\` to start it. Propose them in two separate confirms, one at a time.
   Args: \`{deviceId: "<MAC>", speedName: "S1" | "S2" | "S3" | "S4"}\`
   \`\`\`confirm_action
   {"id":"act_2","action":"select_pump_speed","args":{"deviceId":"286DCE30730F","speedName":"S2"},"summary":"Set EKISS71G_730F's active preset to S2 (1,610 RPM)"}
   \`\`\`

3. **set_preset_rpm** — change the RPM stored in a preset slot (NOT the active speed; this just edits what S2 means). Range 800–3,600 RPM (motor stall floor).
   Args: \`{deviceId: "<MAC>", slot: "S1" | "S2" | "S3" | "S4", rpm: <number>}\`
   \`\`\`confirm_action
   {"id":"act_3","action":"set_preset_rpm","args":{"deviceId":"286DCE30730F","slot":"S2","rpm":1800},"summary":"Set S2 on EKISS71G_730F to 1,800 RPM"}
   \`\`\`

4. **toggle_schedule** — enable or disable a single schedule slot (1, 2, or 3). Other slots untouched.
   Args: \`{deviceId: "<MAC>", scheduleId: "1" | "2" | "3", enabled: true | false}\`
   \`\`\`confirm_action
   {"id":"act_4","action":"toggle_schedule","args":{"deviceId":"286DCE30730F","scheduleId":"3","enabled":false},"summary":"Disable Schedule 3 on EKISS71G_730F"}
   \`\`\`

5. **set_schedule** — edit a schedule slot's enable / start / stop / preset in one go. Any field you omit keeps its current value. Times are 24-hour HH:MM strings — convert from the user's natural-language times yourself ("6pm" → "18:00", "6:30 AM" → "06:30").
   Args: \`{deviceId: "<MAC>", scheduleId: "1"|"2"|"3", enabled?: bool, start?: "HH:MM", stop?: "HH:MM", preset?: "S1"|"S2"|"S3"|"S4"}\`
   \`\`\`confirm_action
   {"id":"act_5","action":"set_schedule","args":{"deviceId":"286DCE30730F","scheduleId":"1","enabled":true,"start":"06:00","stop":"22:00","preset":"S2"},"summary":"Set Schedule 1 on EKISS71G_730F to 6:00 AM–10:00 PM at S2"}
   \`\`\`

ACTION RULES:
- The \`summary\` field is shown to the user verbatim — write it as a short imperative ("Turn pump 730F OFF", "Set S2 to 1,800 RPM"). Use the user-facing pump NAME, not the MAC. Use 12-hour AM/PM times in the summary even though the args use 24-hour.
- Emit ONLY ONE confirm_action per turn unless the user explicitly asked for a batch ("turn off both pumps", "disable all schedules"). When in doubt, propose one and ask if they want to do more.
- DON'T mix \`tool_call\` and \`confirm_action\` fences in the same turn — pick one. If you need data first, do tool_calls; if you have enough info, propose the action.
- After a tool_result for a confirm_action arrives (the action result), acknowledge what happened in plain English. Don't immediately propose another action unless the user asks for one.
- If the user's request is ambiguous (e.g. "turn it on" but they own three pumps and aren't on a specific pump page), use \`activePumpDeviceId\` if available; otherwise ask which pump.
- **Owner-only mutations**: if \`device.ownership === 'following'\`, do NOT propose a mutation — followers can only view. Tell the user politely that only the owner can change settings on a followed pump.
- **Schedule write pre-condition**: schedule mutations (\`toggle_schedule\` / \`set_schedule\`) are silently ignored by the firmware while the pump is running. Before you propose either, check the current power state via \`get_pump_state\`. If the pump is ON, recommend turning it off first ("I can disable Schedule 3, but the pump needs to be off for the firmware to accept the change. Want me to turn it off first, then make the change?") rather than blasting ahead.

TONE:
- Friendly pool tech. Warm, plain English. Brief on routine status answers; multi-step on troubleshooting.
- Refer to a pump by its user-facing name when known, never the MAC address (use the MAC inside tool calls, but show the user the name).
- A pinch of pool humor is welcome but never overplayed.
- No medical / financial / legal advice — out of scope.

STRICT RULES:
- Never claim a mutation succeeded BEFORE the user confirms and the SPA returns a successful tool_result. Always propose via \`confirm_action\` first; never describe an action as done from your own narrative.
- Don't mix mutation domains in a single confirm. If the user asks "turn off the pump and disable Schedule 3", emit ONE confirm (e.g. toggle_pump_power) and offer the second after it lands — pumps can't accept schedule writes while running, so the order matters.
- If the user asks something off-topic (cooking, taxes, etc.), politely redirect to pool topics in one sentence.

WHAT YOU STILL CAN'T DO:
- Register a brand-new pump (\`deviceAdd\`). Goes through the WiFi-provisioning wizard which is mobile-app only.
- Transfer pump ownership to another account.
- Edit user account fields (name, email, password) — those live on the Profile page.`;
}

interface SplashAgentProps {
  /** Optional override for the FAB button label / aria. */
  buttonTitle?: string;
}

// ── Pane layout state ────────────────────────────────────────────
// Persisted across navigation + refresh. Two modes:
//   - floating  → free-positioned with x/y/w/h, drag from header,
//                 resize via the bottom-right corner grip.
//   - anchored  → pinned to the right edge of the viewport, full
//                 height. Width is user-resizable via the left edge.
// Composer height (the textarea) is its own field so user resizes
// of the prompt box also persist.

type PaneMode = 'floating' | 'anchored';

interface PaneState {
  mode: PaneMode;
  /** Floating-only — pixel offsets from top-left of viewport. */
  x: number;
  y: number;
  /** Pane width — used in both modes. */
  w: number;
  /** Floating-only — pane height; anchored mode is always full
   *  viewport height. */
  h: number;
  /** Composer textarea height in px. */
  promptH: number;
}

const PANE_STORAGE_KEY = 'aquastar.user.splash_pane';
const DEFAULT_PANE: PaneState = {
  mode: 'floating',
  x: -1,    // computed on first mount (window.innerWidth - w - 16)
  y: -1,    // computed on first mount (window.innerHeight - h - 16)
  w: 380,
  h: 620,
  promptH: 44,
};
const PANE_MIN_W = 320;
const PANE_MAX_W = 800;
const PANE_MIN_H = 320;
const PROMPT_MIN_H = 36;
const PROMPT_MAX_H = 240;

function loadPaneState(): PaneState {
  try {
    const raw = localStorage.getItem(PANE_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PANE };
    const parsed = JSON.parse(raw) as Partial<PaneState>;
    return {
      mode: parsed.mode === 'anchored' ? 'anchored' : 'floating',
      x: typeof parsed.x === 'number' ? parsed.x : DEFAULT_PANE.x,
      y: typeof parsed.y === 'number' ? parsed.y : DEFAULT_PANE.y,
      w: clamp(parsed.w ?? DEFAULT_PANE.w, PANE_MIN_W, PANE_MAX_W),
      h: Math.max(PANE_MIN_H, parsed.h ?? DEFAULT_PANE.h),
      promptH: clamp(parsed.promptH ?? DEFAULT_PANE.promptH, PROMPT_MIN_H, PROMPT_MAX_H),
    };
  } catch {
    return { ...DEFAULT_PANE };
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

/**
 * Map an upstream `/v1/chat` error to a user-safe chat message.
 *
 * The gateway can produce error text that leaks internal configuration
 * — most notably the Anthropic resolver, which used to surface
 * "Anthropic API key not found: ANTHROPIC_API_KEY is unset and no
 * apiKeyCredentialId / apiKeySecretName configured" verbatim. Customers
 * should never see env-var names. The detail is preserved in console
 * for support to inspect via DevTools; the bubble + banner show
 * something the user can act on (retry, check connection, ping support).
 *
 * Returns `null` for AbortError so the caller can short-circuit
 * silently (user-initiated cancel, route change, etc.).
 */
function friendlyChatErrorMessage(err: unknown): string | null {
  if (err instanceof Error && err.name === 'AbortError') return null;

  const raw = err instanceof Error ? err.message : String(err ?? '');
  const lower = raw.toLowerCase();

  // Network reachability — fetch threw before any HTTP roundtrip, OR the
  // SSE socket dropped mid-stream. Most actionable category for the user.
  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('err_internet') ||
    lower.includes('load failed')
  ) {
    return "I couldn't reach the assistant — check your connection and try again.";
  }

  // The gateway responded with a non-2xx. Status is embedded in the
  // message text we throw upstream in this file ('Gateway returned NNN.').
  const statusMatch = raw.match(/Gateway returned (\d{3})/);
  if (statusMatch) {
    const code = Number(statusMatch[1]);
    if (code === 401 || code === 403) {
      return 'My sign-in expired — please refresh the page and try again.';
    }
    if (code === 429) {
      return "I'm getting too many requests right now — please try again in a moment.";
    }
    if (code >= 500 && code < 600) {
      return "I'm having trouble right now. Please try again in a moment.";
    }
    // 4xx that isn't auth/rate — generally a config / shape problem the
    // user can't fix themselves.
    return "I can't answer right now. Please try again, and let support know if this keeps happening.";
  }

  // Catch-all (includes the Anthropic resolver's auth errors, which
  // arrive as a plain "Anthropic ..." message string with no status
  // prefix). Generic, non-leaky, retryable framing.
  return "I'm having trouble right now. Please try again in a moment.";
}

export function SplashAgent({ buttonTitle = 'Open Splash, the AquaStar assistant' }: SplashAgentProps = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      role: 'assistant',
      content:
        "Hi, I'm Splash — your AquaStar pool-pump helper. Ask me anything about presets, schedules, or troubleshooting your pump.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Chat-history (Phase 2.5) ─────────────────────────────────────
  const { user } = useAuth();
  const userEmail = user?.email ?? null;
  // Active chat id. Hydrated from localStorage in the mount effect
  // below; new sessions start fresh.
  const [chatId, setChatId] = useState<string>(() => newChatId());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [chatsList, setChatsList] = useState<ChatSession[]>([]);
  // Track createdAt for the active chat so saved sessions reflect the
  // actual start time (re-using a chat keeps its original createdAt).
  const [chatCreatedAt, setChatCreatedAt] = useState<string>(() =>
    new Date().toISOString(),
  );

  const [pane, setPane] = useState<PaneState>(loadPaneState);

  // Persist pane state. Throttled-ish via localStorage's natural batching.
  useEffect(() => {
    try {
      localStorage.setItem(PANE_STORAGE_KEY, JSON.stringify(pane));
    } catch { /* quota — ignore */ }
  }, [pane]);

  // ── Chat-history hydration + autosave (Phase 2.5) ─────────────
  // On mount (and on user-email change — in case sign-in lands AFTER
  // mount), load the active chat for this user, if any. Otherwise
  // leave the fresh greeting in place.
  useEffect(() => {
    const activeId = getActiveChatId(userEmail);
    if (!activeId) return;
    const chat = getChat(activeId, userEmail);
    if (!chat) {
      // Stale activeChatId pointing at a deleted chat; clear it.
      setActiveChatId(null, userEmail);
      return;
    }
    setChatId(chat.id);
    setChatCreatedAt(chat.createdAt);
    setMessages(
      chat.messages.map((m) => ({ role: m.role, content: m.content })),
    );
  }, [userEmail]);

  // Autosave on every messages change. Debounced to avoid hammering
  // localStorage on each streamed token. The store's own "drop empty
  // chats" guard means we don't pollute the list with the initial
  // greeting alone.
  useEffect(() => {
    const timer = setTimeout(() => {
      const stored = saveChat(
        {
          id: chatId,
          createdAt: chatCreatedAt,
          messages: messages
            .filter((m) => !m.hidden && (m.role === 'user' || m.role === 'assistant'))
            .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content })),
        },
        userEmail,
      );
      if (stored) {
        setActiveChatId(stored.id, userEmail);
        // Refresh the popover list so a future open shows the new turn.
        setChatsList(listChats(userEmail));
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [messages, chatId, chatCreatedAt, userEmail]);

  // Refresh chats list whenever the popover opens.
  useEffect(() => {
    if (historyOpen) setChatsList(listChats(userEmail));
  }, [historyOpen, userEmail]);

  /** Start a brand-new chat — current one is already auto-saved. */
  const startNewChat = () => {
    const id = newChatId();
    setChatId(id);
    setChatCreatedAt(new Date().toISOString());
    setActiveChatId(id, userEmail);
    setMessages([
      {
        role: 'assistant',
        content:
          "Hi, I'm Splash — your AquaStar pool-pump helper. Ask me anything about presets, schedules, or troubleshooting your pump.",
      },
    ]);
    setInput('');
    setError(null);
    setHistoryOpen(false);
  };

  /** Switch to an existing chat by id. */
  const pickChat = (id: string) => {
    if (id === chatId) {
      setHistoryOpen(false);
      return;
    }
    const chat = getChat(id, userEmail);
    if (!chat) return;
    setChatId(chat.id);
    setChatCreatedAt(chat.createdAt);
    setActiveChatId(chat.id, userEmail);
    setMessages(
      chat.messages.map((m) => ({ role: m.role, content: m.content })),
    );
    setInput('');
    setError(null);
    setHistoryOpen(false);
  };

  // First-open: if x/y were never set (-1), compute a default
  // bottom-right anchor for the floating mode that respects the
  // current viewport size.
  useEffect(() => {
    if (!isOpen) return;
    setPane((p) => {
      if (p.mode !== 'floating') return p;
      if (p.x >= 0 && p.y >= 0) return p;
      const w = clamp(p.w, PANE_MIN_W, Math.min(PANE_MAX_W, window.innerWidth - 32));
      const h = Math.max(PANE_MIN_H, Math.min(p.h, window.innerHeight - 32));
      return {
        ...p,
        w,
        h,
        x: Math.max(8, window.innerWidth - w - 16),
        y: Math.max(8, window.innerHeight - h - 16),
      };
    });
  }, [isOpen]);

  // Keep the floating pane on-screen if the window resizes.
  useEffect(() => {
    function onResize() {
      setPane((p) => {
        if (p.mode !== 'floating') return p;
        if (p.x < 0 || p.y < 0) return p;
        return {
          ...p,
          x: clamp(p.x, 8, Math.max(8, window.innerWidth - 80)),
          y: clamp(p.y, 8, Math.max(8, window.innerHeight - 80)),
        };
      });
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Scroll the message list to the bottom whenever it grows.
  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  // Cancel any in-flight request when the panel unmounts.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // Cap on tool round-trips per user message. Stops a runaway loop
  // where the model keeps emitting tool_calls instead of answering.
  const MAX_TOOL_ROUNDTRIPS = 4;

  async function handleSend(rawText?: string) {
    const text = (rawText ?? input).trim();
    if (!text || loading) return;
    setError(null);
    setInput('');

    const userMessage: ChatMessage = { role: 'user', content: text };
    // Snapshot the conversation BEFORE we add the empty assistant
    // bubble — that's what gets sent to the Gateway.
    let conversation: ChatMessage[] = [...messages, userMessage];
    setMessages([...conversation, { role: 'assistant', content: '' }]);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    const today = new Date().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const systemPrompt = buildSystemPrompt(today);

    // Cap history sent upstream to keep token usage modest. The user
    // sees the full transcript locally; only the last ~12 turns go
    // out per request.
    const HISTORY_CAP = 12;

    const token = getToken();
    const startedAt = performance.now();

    // Snapshot the UI context (route + active pump) at submit time.
    // Re-read on each tool round-trip so a navigation mid-conversation
    // updates which pump Splash treats as implicit.
    const initialUiContext = readUiContext();

    try {
      // ─── Tool dispatch loop ────────────────────────────────────
      // Each iteration: post one /v1/chat round-trip, stream the
      // assistant response, parse it for `tool_call` fences. If any
      // are present (and we haven't hit the iteration cap), execute
      // the tools, append a synthetic user message carrying the
      // `tool_result` fences, and loop. If none, break and finish.
      // The cap stops a runaway model from churning forever.
      let toolRoundtrips = 0;
      // Tool-loop termination is driven by 4 internal break paths
      // (no-tool-calls / cap-reached / empty-stream / pending-confirm-
      // actions), so the condition itself is just the flag. Kept as a
      // labeled flag (not a do/while) because each break path has its
      // own state-setup before exit.
      let toolLoopDone = false;
      while (!toolLoopDone) {
        // Re-read UI context on every iteration — the user might
        // navigate to a different pump page mid-conversation.
        const uiContext = toolRoundtrips === 0 ? initialUiContext : readUiContext();
        const uiContextLine = `UI CONTEXT — ${describeUiContext(uiContext)}`;

        // Inject the UI context as a transient system note JUST
        // before the user's latest input. It's not stored in
        // `conversation` (so it doesn't bloat history), but the
        // model sees it on every turn — including tool round-trips.
        const trimmedHistory = conversation.slice(-HISTORY_CAP);
        const messagesForApi: { role: 'system' | 'user' | 'assistant'; content: string }[] = [
          { role: 'system', content: systemPrompt },
          { role: 'system', content: uiContextLine },
          ...trimmedHistory.map((m) => ({ role: m.role, content: m.content })),
        ];

        // Only include provider/model when explicitly set (left undefined
        // above so Nexus uses the default provider). Spread-undefined
        // would still send the key with value:undefined which JSON.stringify
        // drops — but being explicit + symmetric here.
        const body: Record<string, unknown> = {
          requestId: cryptoRandomUuid(),
          appId: APP_ID,
          stream: true,
          maxTokens: 1024,
          messages: messagesForApi,
        };
        if (PROVIDER) body.provider = PROVIDER;
        if (MODEL) body.model = MODEL;

        const res = await fetch(`${GATEWAY_BASE}/v1/chat`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const errText = await res.text().catch(() => '');
          let nestedMessage: string | null = null;
          try {
            const parsed = JSON.parse(errText) as { error?: { message?: string } | string };
            if (typeof parsed?.error === 'string') nestedMessage = parsed.error;
            else if (parsed?.error?.message) nestedMessage = parsed.error.message;
          } catch {
            /* keep nestedMessage null */
          }
          throw new Error(nestedMessage ?? `Gateway returned ${res.status}.`);
        }
        if (!res.body) {
          throw new Error('Gateway returned no streaming body.');
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let assistantText = '';
        let sawAnyToken = false;

        // ── Rotating "thinking" musings ─────────────────────────
        // While we're waiting for the first token, cycle through
        // pool-themed phrases every ~2.5s and tick a live elapsed
        // counter every 500ms. Cleared the moment a real token
        // lands (sawAnyToken flips) or the stream errors out.
        const turnStartedAt = Date.now();
        let currentMusingIdx = pickMusing(-1);
        let currentMusingShownAt = turnStartedAt;
        const renderThinking = () => {
          const elapsedMs = Date.now() - turnStartedAt;
          const seconds = Math.floor(elapsedMs / 1000);
          const phrase = SPLASH_MUSINGS[currentMusingIdx];
          const text = `*${phrase}… (${seconds}s)*`;
          setMessages((prev) => {
            const next = [...prev];
            // Only update if the bubble is still in its placeholder
            // state — otherwise we'd overwrite real tokens that
            // raced our interval tick.
            const last = next[next.length - 1];
            if (!last || last.role !== 'assistant') return prev;
            // If the bubble has actual content (not just our prior
            // italic-musing line), don't clobber it.
            if (last.content.length > 0 && !last.content.startsWith('*')) {
              return prev;
            }
            next[next.length - 1] = { role: 'assistant', content: text };
            return next;
          });
        };
        // Paint immediately so the user sees something on submit.
        renderThinking();
        const tick = window.setInterval(() => {
          if (sawAnyToken) {
            window.clearInterval(tick);
            return;
          }
          // Rotate to a new musing every ~2.5s. Suppresses immediate
          // repeats via pickMusing's exclude param.
          if (Date.now() - currentMusingShownAt > 2500) {
            currentMusingIdx = pickMusing(currentMusingIdx);
            currentMusingShownAt = Date.now();
          }
          renderThinking();
        }, 500);

        try {
          let streamDone = false;
          while (!streamDone) {
            const { done, value } = await reader.read();
            if (done) {
              streamDone = true;
              continue;
            }
            buffer += decoder.decode(value, { stream: true });

            // SSE framing: events terminated by blank line. Split on
            // "\n\n", keep the trailing partial in `buffer`.
            const parts = buffer.split('\n\n');
            buffer = parts.pop() ?? '';

            for (const rawPart of parts) {
              const part = rawPart.trim();
              if (!part.startsWith('data: ')) continue;
              const payload = part.slice(6);
              if (payload === '[DONE]') {
                // Reader will drain; loop will exit on next read.
                continue;
              }
              let evt: any;
              try { evt = JSON.parse(payload); } catch { continue; }

              // Surface server-side errors mid-stream.
              if (evt?.error) {
                throw new Error(
                  typeof evt.error === 'string'
                    ? evt.error
                    : evt.error?.message || JSON.stringify(evt.error),
                );
              }
              // Heartbeat — keep-alive while the upstream model thinks.
              // The musings rotator above is the visible response;
              // we just acknowledge the heartbeat and continue.
              if (evt?.type === 'kstack_heartbeat') continue;

              // OpenAI-style delta tokens.
              const delta: string | undefined = evt?.choices?.[0]?.delta?.content;
              if (typeof delta === 'string' && delta.length > 0) {
                sawAnyToken = true;
                assistantText += delta;
                setMessages((prev) => {
                  const next = [...prev];
                  next[next.length - 1] = { role: 'assistant', content: assistantText };
                  return next;
                });
              }
              // kstack_summary carries final usage; we don't render it in v0.
            }
          }
        } finally {
          window.clearInterval(tick);
        }

        if (!sawAnyToken && assistantText.length === 0) {
          // Empty-stream guard — same scenario Vault hit when the
          // Gateway swallowed a tool error silently. Surface a useful
          // error rather than a blank bubble.
          setMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = {
              role: 'assistant',
              content:
                "I didn't get a response back from the model. That's almost always a transient Gateway issue — try again in a moment.",
              error: true,
            };
            return next;
          });
          break;
        }

        // ── Parse confirm_action fences first ──────────────────
        // If the assistant is proposing a mutation, we DON'T loop —
        // we exit and let the user click Confirm/Cancel. The action
        // is stored on the assistant message; the inline UI is
        // rendered from there. When the user decides, the decision
        // handler appends a synthetic tool_result user message and
        // calls handleSend to resume.
        const confirmActions = parseConfirmActions(assistantText);

        // Lock in this turn's assistant text into the conversation
        // history. Subsequent iterations send it as prior context.
        const assistantMsg: ChatMessage = {
          role: 'assistant',
          content: assistantText,
          ...(confirmActions.length > 0
            ? {
                pendingActions: confirmActions,
                actionDecisions: {},
              }
            : {}),
        };
        conversation = [...conversation, assistantMsg];

        if (confirmActions.length > 0) {
          // Reflect the pendingActions into rendered state — the
          // bubble will show inline Confirm/Cancel buttons.
          setMessages((prev) => {
            const next = [...prev];
            // Replace the last assistant bubble (it was streaming
            // into next[next.length - 1]) with the pending-actions
            // version.
            next[next.length - 1] = assistantMsg;
            return next;
          });
          // Stop the loop. handleConfirmActionDecision picks up
          // when the user clicks a button.
          break;
        }

        // ── Parse + execute tool calls if any ────────────────
        const toolCalls = parseToolCalls(assistantText);
        if (toolCalls.length === 0) {
          // No more tools requested → assistant is done.
          break;
        }
        if (toolRoundtrips >= MAX_TOOL_ROUNDTRIPS) {
          // Hit cap — append a synthetic tool_result fence noting
          // the limit. The model will hopefully wrap up rather
          // than keep asking.
          const cappedResults = toolCalls.map((c) => ({
            id: c.id,
            ok: false,
            error: `Tool round-trip cap (${MAX_TOOL_ROUNDTRIPS}) reached. Please answer with what you have.`,
          }));
          const cappedText = renderToolResults(cappedResults);
          conversation = [...conversation, { role: 'user', content: cappedText }];
          setMessages([...conversation, {
            role: 'assistant',
            content: '',
          }]);
          // Don't continue — just break and let the loop end with
          // whatever the assistant said previously.
          break;
        }

        toolRoundtrips++;
        // eslint-disable-next-line no-console
        console.debug('[Splash] dispatching tool calls', toolCalls.map((c) => c.tool));
        const results = await Promise.all(
          toolCalls.map(async (c) => {
            try {
              const data = await runTool(c);
              return { id: c.id, ok: true as const, data };
            } catch (err) {
              return {
                id: c.id,
                ok: false as const,
                error: err instanceof Error ? err.message : String(err),
              };
            }
          }),
        );

        // Append a synthetic user message with tool_result fences,
        // then a fresh empty assistant bubble for the next stream.
        const resultsText = renderToolResults(results);
        conversation = [...conversation, { role: 'user', content: resultsText }];
        setMessages([
          ...conversation,
          { role: 'assistant', content: '' },
        ]);

        // Loop back for another /v1/chat round-trip.
      }
    } catch (err) {
      // Preserve the raw upstream detail for support / DevTools.
      // The user-visible message is the sanitized version below — the
      // gateway can emit text that names internal env vars or
      // credential ids, which customers should never see.
      // eslint-disable-next-line no-console
      console.error('[Splash] chat request failed', err);

      const friendly = friendlyChatErrorMessage(err);
      if (friendly === null) {
        // AbortError — user cancelled / route change. Silent.
        return;
      }
      setError(friendly);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          content: friendly,
          error: true,
        };
        return next;
      });
    } finally {
      const ms = Math.round(performance.now() - startedAt);
      // eslint-disable-next-line no-console
      console.debug('[Splash] turn complete', { ms });
      setLoading(false);
      abortRef.current = null;
    }
  }

  function onCancel() {
    abortRef.current?.abort();
  }

  /**
   * User clicked Confirm or Cancel on a pending action inside an
   * assistant bubble. Run the mutation if confirmed (or skip if
   * cancelled), then ship the outcome back to Splash as a synthetic
   * tool_result user message and resume the chat loop.
   *
   * `messageIdx` is the index of the assistant message that owns
   * this action. `actionId` matches the `id` Splash assigned in its
   * confirm_action fence.
   */
  async function handleActionDecision(
    messageIdx: number,
    actionId: string,
    decision: 'confirmed' | 'cancelled',
  ) {
    if (loading) return;

    // Find the message + action.
    const msg = messages[messageIdx];
    if (!msg || !msg.pendingActions) return;
    const action = msg.pendingActions.find((a) => a.id === actionId);
    if (!action) return;
    if (msg.actionDecisions?.[actionId] && msg.actionDecisions[actionId] !== 'cancelled') {
      // Already running or done — don't re-run.
      return;
    }

    // Mark in-flight in state so the buttons disable.
    setMessages((prev) => {
      const next = [...prev];
      const m = next[messageIdx];
      if (!m) return prev;
      next[messageIdx] = {
        ...m,
        actionDecisions: {
          ...(m.actionDecisions ?? {}),
          [actionId]: decision === 'confirmed' ? 'running' : 'cancelled',
        },
      };
      return next;
    });

    // Execute (or skip).
    let result: ToolResult;
    if (decision === 'cancelled') {
      result = {
        id: actionId,
        ok: false,
        error: 'user cancelled',
      };
      // Mark as cancelled (final state).
      setMessages((prev) => {
        const next = [...prev];
        const m = next[messageIdx];
        if (!m) return prev;
        next[messageIdx] = {
          ...m,
          actionDecisions: { ...(m.actionDecisions ?? {}), [actionId]: 'cancelled' },
        };
        return next;
      });
    } else {
      try {
        const data = await runMutation(action);
        result = { id: actionId, ok: true, data };
        setMessages((prev) => {
          const next = [...prev];
          const m = next[messageIdx];
          if (!m) return prev;
          next[messageIdx] = {
            ...m,
            actionDecisions: { ...(m.actionDecisions ?? {}), [actionId]: 'done' },
          };
          return next;
        });
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        result = { id: actionId, ok: false, error: errMsg };
        setMessages((prev) => {
          const next = [...prev];
          const m = next[messageIdx];
          if (!m) return prev;
          next[messageIdx] = {
            ...m,
            actionDecisions: { ...(m.actionDecisions ?? {}), [actionId]: 'error' },
            actionErrors: { ...(m.actionErrors ?? {}), [actionId]: errMsg },
          };
          return next;
        });
      }
    }

    // Ship the outcome back to Splash via the existing tool_result
    // convention. handleSend treats the synthetic content as a
    // normal user turn and the chat loop resumes.
    const resultsText = renderToolResults([result]);
    void handleSend(resultsText);
  }

  // ── Drag (floating mode only) ─────────────────────────────────
  const onHeaderPointerDown = (e: React.PointerEvent) => {
    if (pane.mode !== 'floating') return;
    // Don't start a drag if the pointer landed on a button inside
    // the header — buttons handle their own clicks.
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPaneX = pane.x;
    const startPaneY = pane.y;

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setPane((p) => ({
        ...p,
        x: clamp(startPaneX + dx, 8, Math.max(8, window.innerWidth - 80)),
        y: clamp(startPaneY + dy, 8, Math.max(8, window.innerHeight - 40)),
      }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ── Resize handle: floating-mode bottom-right corner ──────────
  // Drags both width and height at once.
  const onCornerResizeDown = (e: React.PointerEvent) => {
    if (pane.mode !== 'floating') return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = pane.w;
    const startH = pane.h;
    const onMove = (ev: PointerEvent) => {
      setPane((p) => ({
        ...p,
        w: clamp(startW + (ev.clientX - startX), PANE_MIN_W, Math.min(PANE_MAX_W, window.innerWidth - p.x - 8)),
        h: Math.max(PANE_MIN_H, Math.min(startH + (ev.clientY - startY), window.innerHeight - p.y - 8)),
      }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ── Resize handle: anchored-mode left edge ────────────────────
  // Only affects width — height is locked to viewport when anchored.
  const onLeftEdgeResizeDown = (e: React.PointerEvent) => {
    if (pane.mode !== 'anchored') return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = pane.w;
    const onMove = (ev: PointerEvent) => {
      // Anchored to the right edge → dragging left grows width.
      setPane((p) => ({
        ...p,
        w: clamp(startW - (ev.clientX - startX), PANE_MIN_W, Math.min(PANE_MAX_W, window.innerWidth - 16)),
      }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ── Toggle anchor / float ─────────────────────────────────────
  const toggleAnchor = () => {
    setPane((p) => {
      if (p.mode === 'anchored') {
        // Anchored → floating: drop into the bottom-right slot at the
        // user's last anchored width.
        const w = clamp(p.w, PANE_MIN_W, Math.min(PANE_MAX_W, window.innerWidth - 32));
        const h = Math.max(PANE_MIN_H, Math.min(p.h || 620, window.innerHeight - 32));
        return {
          ...p,
          mode: 'floating',
          w,
          h,
          x: Math.max(8, window.innerWidth - w - 16),
          y: Math.max(8, window.innerHeight - h - 16),
        };
      }
      // Floating → anchored: keep the user's width (clamped),
      // height becomes implicit (viewport-locked).
      return {
        ...p,
        mode: 'anchored',
        w: clamp(p.w, PANE_MIN_W, Math.min(PANE_MAX_W, window.innerWidth - 16)),
      };
    });
  };

  // Position styles vary by mode.
  const paneStyle: React.CSSProperties =
    pane.mode === 'anchored'
      ? {
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: pane.w,
          height: '100vh',
          background: 'var(--aqua-surface)',
          borderLeft: '1px solid var(--aqua-border)',
          borderRadius: 0,
          boxShadow: 'var(--aqua-shadow-md, -4px 0 24px rgba(0,0,0,0.18))',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 1040,
        }
      : {
          position: 'fixed',
          left: pane.x >= 0 ? pane.x : undefined,
          top: pane.y >= 0 ? pane.y : undefined,
          right: pane.x < 0 ? 16 : undefined,
          bottom: pane.y < 0 ? 16 : undefined,
          width: pane.w,
          height: pane.h,
          background: 'var(--aqua-surface)',
          border: '1px solid var(--aqua-border)',
          borderRadius: 14,
          boxShadow: 'var(--aqua-shadow-md, 0 10px 30px rgba(0,0,0,0.22))',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 1040,
        };

  return (
    <>
      {/* ── Floating action button ──────────────────────────────── */}
      {!isOpen ? (
        <button
          type="button"
          aria-label={buttonTitle}
          title={buttonTitle}
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            width: 56,
            height: 56,
            borderRadius: '50%',
            border: 'none',
            background: 'var(--aqua-primary)',
            color: '#fff',
            boxShadow: 'var(--aqua-shadow-md, 0 6px 20px rgba(0,0,0,0.18))',
            cursor: 'pointer',
            zIndex: 1040,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SplashIcon size={28} />
        </button>
      ) : null}

      {/* ── Docked / floating panel ─────────────────────────────── */}
      {isOpen ? (
        <aside
          role="dialog"
          aria-label="Splash chat"
          style={paneStyle}
        >
          {/* Left-edge resize bar (anchored mode only) */}
          {pane.mode === 'anchored' ? (
            <div
              onPointerDown={onLeftEdgeResizeDown}
              aria-label="Resize Splash width"
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 6,
                cursor: 'ew-resize',
                background: 'transparent',
                zIndex: 2,
              }}
            />
          ) : null}

          {/* Header — drag handle + anchor toggle + close */}
          <header
            onPointerDown={onHeaderPointerDown}
            style={{
              padding: '12px 14px',
              background: 'var(--aqua-navy)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: pane.mode === 'floating' ? 'grab' : 'default',
              userSelect: 'none',
              touchAction: 'none',
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <SplashIcon size={20} />
              <strong style={{ letterSpacing: '0.02em' }}>Splash</strong>
              <span style={{ opacity: 0.7, fontSize: '0.8rem' }}>
                AquaStar assistant
              </span>
            </div>
            <div className="d-flex align-items-center gap-1" style={{ position: 'relative' }}>
              {/* + new-chat */}
              <button
                type="button"
                onClick={startNewChat}
                aria-label="Start a new chat"
                title="New chat"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  padding: 4,
                  lineHeight: 1,
                  opacity: 0.85,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
              {/* clock-rewind: past-chats popover */}
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                aria-label="Past chats"
                aria-expanded={historyOpen}
                title="Past chats"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  padding: 4,
                  lineHeight: 1,
                  opacity: 0.85,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  {/* clock face */}
                  <circle cx="12" cy="13" r="7" />
                  <polyline points="12 9 12 13 15 14" />
                  {/* counter-clockwise arrow loop top-left */}
                  <polyline points="3 7 7 7 7 3" />
                  <path d="M7 7 a7 7 0 0 1 5 -3" />
                </svg>
              </button>
              {historyOpen ? (
                <ChatHistoryPopover
                  chats={chatsList}
                  activeChatId={chatId}
                  onPick={pickChat}
                  onClose={() => setHistoryOpen(false)}
                  onChatsChanged={() => setChatsList(listChats(userEmail))}
                  userEmail={userEmail}
                />
              ) : null}
              <button
                type="button"
                onClick={toggleAnchor}
                aria-label={
                  pane.mode === 'anchored'
                    ? 'Detach Splash from the right edge'
                    : 'Anchor Splash to the right edge'
                }
                title={
                  pane.mode === 'anchored'
                    ? 'Detach (free-floating)'
                    : 'Anchor to right edge'
                }
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  padding: 4,
                  lineHeight: 1,
                  opacity: 0.85,
                }}
              >
                {pane.mode === 'anchored' ? (
                  // "Detach" glyph — a window peeling off the right edge
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                ) : (
                  // "Anchor right" glyph — three lines + arrow into right edge
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="21" y1="3" x2="21" y2="21" />
                    <polyline points="13 6 19 12 13 18" />
                    <line x1="3" y1="12" x2="19" y2="12" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                aria-label="Close Splash"
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: 4,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          </header>

          {/* Messages list */}
          <div
            style={{
              flex: '1 1 auto',
              overflowY: 'auto',
              padding: '12px 14px',
              background: 'var(--aqua-bg)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                message={m}
                messageIdx={i}
                onActionDecision={handleActionDecision}
                disabled={loading}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {error ? (
            <div
              role="alert"
              style={{
                padding: '8px 14px',
                background: 'rgba(220,53,69,0.08)',
                color: '#dc3545',
                fontSize: '0.85rem',
                borderTop: '1px solid var(--aqua-border)',
              }}
            >
              {error}
            </div>
          ) : null}

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSend();
            }}
            style={{
              borderTop: '1px solid var(--aqua-border)',
              padding: 10,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-end',
              background: 'var(--aqua-surface)',
            }}
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // Enter to send, Shift+Enter for newline (matches most chat UIs).
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              onMouseUp={(e) => {
                // Browser native vertical-resize updates the offsetHeight
                // — capture the user's pick on mouseup so it persists.
                const next = clamp((e.currentTarget as HTMLTextAreaElement).offsetHeight, PROMPT_MIN_H, PROMPT_MAX_H);
                if (next !== pane.promptH) {
                  setPane((p) => ({ ...p, promptH: next }));
                }
              }}
              placeholder="Ask Splash about your pump…"
              disabled={loading}
              className="form-control aqua-input"
              style={{
                resize: 'vertical',
                height: pane.promptH,
                minHeight: PROMPT_MIN_H,
                maxHeight: PROMPT_MAX_H,
                fontSize: '0.92rem',
              }}
              aria-label="Message Splash"
            />
            {loading ? (
              <button
                type="button"
                onClick={onCancel}
                className="btn btn-outline-secondary btn-sm"
                aria-label="Stop generation"
              >
                Stop
              </button>
            ) : (
              <button
                type="submit"
                disabled={!input.trim()}
                className="btn btn-aqua-navy btn-sm"
                aria-label="Send message"
              >
                Send
              </button>
            )}
          </form>

          {/* Bottom-right corner resize grip (floating mode only) */}
          {pane.mode === 'floating' ? (
            <div
              onPointerDown={onCornerResizeDown}
              aria-label="Resize Splash"
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: 16,
                height: 16,
                cursor: 'nwse-resize',
                background:
                  'linear-gradient(135deg, transparent 0 50%, var(--aqua-border) 50% 60%, transparent 60% 70%, var(--aqua-border) 70% 80%, transparent 80% 90%, var(--aqua-border) 90% 100%)',
                borderBottomRightRadius: 14,
                zIndex: 2,
              }}
            />
          ) : null}
        </aside>
      ) : null}
    </>
  );
}

// Markdown component overrides for the chat bubble. Module-scope so
// React-Markdown sees stable identity across renders. Tightens
// spacing (default <p> margin is ~1rem — too airy for a chat bubble),
// scopes Bootstrap table classes onto rendered GFM tables, and styles
// inline + block code. We deliberately do NOT pass `rehypeRaw` —
// raw HTML in assistant output stays escaped (XSS hardening).
const MARKDOWN_COMPONENTS: Components = {
  p: ({ node: _node, ...props }) => (
    <p style={{ margin: '0 0 6px 0' }} {...props} />
  ),
  ul: ({ node: _node, ...props }) => (
    <ul style={{ margin: '0 0 6px 0', paddingLeft: 20 }} {...props} />
  ),
  ol: ({ node: _node, ...props }) => (
    <ol style={{ margin: '0 0 6px 0', paddingLeft: 20 }} {...props} />
  ),
  li: ({ node: _node, ...props }) => (
    <li style={{ marginBottom: 2 }} {...props} />
  ),
  h1: ({ node: _node, ...props }) => (
    <h5 style={{ margin: '4px 0 6px 0', fontSize: '1.05rem', fontWeight: 600 }} {...props} />
  ),
  h2: ({ node: _node, ...props }) => (
    <h6 style={{ margin: '4px 0 6px 0', fontSize: '1rem', fontWeight: 600 }} {...props} />
  ),
  h3: ({ node: _node, ...props }) => (
    <h6 style={{ margin: '4px 0 4px 0', fontSize: '0.95rem', fontWeight: 600 }} {...props} />
  ),
  table: ({ node: _node, ...props }) => (
    <div style={{ overflowX: 'auto', margin: '6px 0' }}>
      <table
        className="table table-sm table-bordered"
        style={{ fontSize: '0.85rem', marginBottom: 0 }}
        {...props}
      />
    </div>
  ),
  th: ({ node: _node, ...props }) => (
    <th style={{ background: 'rgba(0,0,0,0.04)', whiteSpace: 'nowrap' }} {...props} />
  ),
  td: ({ node: _node, ...props }) => (
    <td style={{ verticalAlign: 'top' }} {...props} />
  ),
  code: ({ node: _node, className, children, ...props }) => {
    const isBlock = /\blanguage-/.test(className ?? '');
    if (isBlock) {
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        style={{
          background: 'rgba(0,0,0,0.06)',
          padding: '1px 4px',
          borderRadius: 3,
          fontSize: '0.85em',
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ node: _node, ...props }) => (
    <pre
      style={{
        background: 'rgba(0,0,0,0.04)',
        padding: 8,
        borderRadius: 6,
        overflowX: 'auto',
        fontSize: '0.85em',
        margin: '6px 0',
      }}
      {...props}
    />
  ),
  a: ({ node: _node, ...props }) => (
    <a target="_blank" rel="noopener noreferrer" {...props} />
  ),
  blockquote: ({ node: _node, ...props }) => (
    <blockquote
      style={{
        borderLeft: '3px solid var(--aqua-border, rgba(0,0,0,0.1))',
        paddingLeft: 10,
        margin: '6px 0',
        opacity: 0.85,
      }}
      {...props}
    />
  ),
};

function MessageBubble({
  message,
  messageIdx,
  onActionDecision,
  disabled,
}: {
  message: ChatMessage;
  messageIdx: number;
  onActionDecision: (
    messageIdx: number,
    actionId: string,
    decision: 'confirmed' | 'cancelled',
  ) => void | Promise<void>;
  disabled: boolean;
}) {
  const { role, content, error, pendingActions, actionDecisions, actionErrors } = message;
  if (role === 'system') return null;
  if (message.hidden) return null;

  // User messages whose entire content is one or more `tool_result`
  // fences are synthetic — they're how the SPA replies to Splash
  // after a tool call or confirmed action. Hide them from view.
  if (role === 'user' && /^\s*```tool_result\b/.test(content) && /```\s*$/.test(content)) {
    return null;
  }

  // For assistant messages we splice the bubble into ordered prose +
  // DataView segments so a `data_view` fence renders inline at its
  // original position. User messages stay simple — they never have
  // data_view fences and synthetic tool_result-only user messages were
  // already early-returned above.
  const isUser = role === 'user';
  const segments = isUser ? null : buildBubbleSegments(content);

  // The "is bubble visible at all?" check needs ALL fences stripped,
  // because a bubble made up only of fences (no prose, no view, no
  // pending action) is conversation plumbing and shouldn't render.
  const fullyStripped = content.replace(ALL_FENCES_STRIP_RE, '').trim();
  const userVisibleContent = isUser ? fullyStripped : '';
  const hasPending = (pendingActions?.length ?? 0) > 0;
  const hasAnyContent = isUser
    ? userVisibleContent.length > 0
    : (segments?.length ?? 0) > 0;

  if (!hasAnyContent && !hasPending && content.length > 0) {
    return null;
  }

  const bg = error
    ? 'rgba(220,53,69,0.1)'
    : isUser
      ? 'var(--aqua-primary)'
      : 'var(--aqua-surface-muted, var(--aqua-bg))';
  const color = error
    ? '#dc3545'
    : isUser
      ? '#fff'
      : 'var(--aqua-text)';

  return (
    <div
      style={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '85%',
        background: bg,
        color,
        padding: '8px 12px',
        borderRadius: 12,
        borderTopRightRadius: isUser ? 2 : 12,
        borderTopLeftRadius: isUser ? 12 : 2,
        // User bubbles: pre-wrap so manually-typed line breaks survive.
        // Assistant bubbles: 'normal' — react-markdown emits its own
        // block elements with their own spacing; pre-wrap on top of
        // that doubles paragraph gaps.
        whiteSpace: isUser ? 'pre-wrap' : 'normal',
        lineHeight: 1.45,
        fontSize: '0.92rem',
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
      }}
    >
      {isUser ? (
        // Plain text for user messages — they don't typically write
        // markdown, and we don't want their prose accidentally
        // re-interpreted (e.g. underscores in pump MACs).
        userVisibleContent
      ) : segments && segments.length > 0 ? (
        segments.map((seg, i) =>
          seg.kind === 'text' ? (
            <ReactMarkdown
              key={i}
              remarkPlugins={[remarkGfm]}
              components={MARKDOWN_COMPONENTS}
            >
              {seg.text}
            </ReactMarkdown>
          ) : (
            <DataView key={i} spec={seg.spec} raw={seg.raw} />
          ),
        )
      ) : (
        <em style={{ opacity: 0.6 }}>…</em>
      )}

      {hasPending ? (
        <div
          style={{
            marginTop: hasAnyContent ? 10 : 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {pendingActions!.map((action) => {
            const decision = actionDecisions?.[action.id];
            const errMsg = actionErrors?.[action.id];
            const isRunning = decision === 'running';
            const isDone = decision === 'done';
            const isCancelled = decision === 'cancelled';
            const isErrored = decision === 'error';
            const settled = isDone || isCancelled || isErrored;
            return (
              <div
                key={action.id}
                style={{
                  border: '1px solid var(--aqua-border)',
                  borderRadius: 8,
                  padding: 8,
                  background: 'var(--aqua-surface)',
                  color: 'var(--aqua-text)',
                  fontSize: '0.88rem',
                }}
              >
                <div style={{ marginBottom: 6, fontWeight: 600 }}>
                  {action.summary}
                </div>
                {settled ? (
                  <div
                    style={{
                      fontSize: '0.82rem',
                      color: isDone
                        ? '#198754'
                        : isCancelled
                          ? 'var(--aqua-text-muted)'
                          : '#dc3545',
                    }}
                  >
                    {isDone
                      ? '✓ Done'
                      : isCancelled
                        ? '✗ Cancelled'
                        : `✗ Failed${errMsg ? ` — ${errMsg}` : ''}`}
                  </div>
                ) : (
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-aqua-navy btn-sm"
                      onClick={() =>
                        void onActionDecision(messageIdx, action.id, 'confirmed')
                      }
                      disabled={disabled || isRunning}
                    >
                      {isRunning ? 'Working…' : 'Confirm'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() =>
                        void onActionDecision(messageIdx, action.id, 'cancelled')
                      }
                      disabled={disabled || isRunning}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** Minimalist pool/water-droplet glyph for Splash branding. */
function SplashIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </svg>
  );
}

/**
 * Pick a random index into SPLASH_MUSINGS, excluding the one we
 * just showed so we don't immediately repeat. Pass -1 to allow
 * any index (first call).
 */
function pickMusing(excludeIdx: number): number {
  const len = SPLASH_MUSINGS.length;
  if (len <= 1) return 0;
  // Pick from len-1 candidates, skipping excludeIdx so we don't
  // repeat. Simple uniform.
  let idx = Math.floor(Math.random() * (len - (excludeIdx >= 0 ? 1 : 0)));
  if (excludeIdx >= 0 && idx >= excludeIdx) idx += 1;
  return idx;
}

// ── Tool dispatch helpers ───────────────────────────────────────────
// Splash emits ```tool_call JSON fences when it needs server data;
// the SPA parses them, runs the matching app-fn, and replies with a
// ```tool_result fence in a synthetic user message. This block is
// the parser + executor + renderer.

interface ParsedToolCall {
  id: string;
  tool: string;
  args: Record<string, unknown>;
}

interface ToolResult {
  id: string;
  ok: boolean;
  data?: unknown;
  error?: string;
}

const TOOL_CALL_FENCE_RE = /```tool_call\s*\n([\s\S]*?)\n```/g;
const CONFIRM_ACTION_FENCE_RE = /```confirm_action\s*\n([\s\S]*?)\n```/g;
// Used to strip plumbing fences out of the assistant content for
// markdown rendering — the JSON is conversation plumbing, not
// user-visible text. NOTE: data_view fences are NOT in this regex —
// those get pulled out and rendered as React components inline (see
// buildBubbleSegments below).
const FENCE_STRIP_RE = /```(?:tool_call|tool_result|confirm_action)\s*\n[\s\S]*?\n```\s*/g;
// Same set, plus data_view, for the "is this bubble entirely empty?"
// check. A bubble made up only of fences should not render — except
// when there's a pending confirm action attached (handled separately).
const ALL_FENCES_STRIP_RE =
  /```(?:tool_call|tool_result|confirm_action|data_view)\s*\n[\s\S]*?\n```\s*/g;

// Ordered render plan for an assistant bubble: prose runs interleaved
// with DataView components. data_view fences come out at their original
// positions so the surrounding prose ("Here's the last 9 events…")
// renders BEFORE the table, and any follow-up text AFTER it.
//
// Parameters:
//   content — the raw assistant message string (still includes all
//             fence types).
// Returns:
//   Ordered list of segments. Empty list when the bubble has nothing
//   user-visible (caller drops the bubble).
type BubbleSegment =
  | { kind: 'text'; text: string }
  | { kind: 'view'; spec: DataViewSpec | null; raw: string };

function buildBubbleSegments(content: string): BubbleSegment[] {
  const matches = findDataViewFences(content);
  const segments: BubbleSegment[] = [];
  let cursor = 0;
  for (const m of matches) {
    if (m.start > cursor) {
      // Strip plumbing fences (tool_call/tool_result/confirm_action)
      // from the prose run BEFORE this data_view, but leave anything
      // else intact for markdown rendering.
      const text = content
        .slice(cursor, m.start)
        .replace(FENCE_STRIP_RE, '')
        .trim();
      if (text) segments.push({ kind: 'text', text });
    }
    segments.push({ kind: 'view', spec: m.spec, raw: m.raw });
    cursor = m.end;
  }
  if (cursor < content.length) {
    const tail = content.slice(cursor).replace(FENCE_STRIP_RE, '').trim();
    if (tail) segments.push({ kind: 'text', text: tail });
  }
  return segments;
}

function parseToolCalls(assistantText: string): ParsedToolCall[] {
  const calls: ParsedToolCall[] = [];
  // Reset regex lastIndex defensively (top-level regex object).
  TOOL_CALL_FENCE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOOL_CALL_FENCE_RE.exec(assistantText)) !== null) {
    const json = match[1].trim();
    let parsed: any;
    try { parsed = JSON.parse(json); } catch { continue; }
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof parsed.tool !== 'string'
    ) {
      continue;
    }
    calls.push({
      id: typeof parsed.id === 'string' ? parsed.id : `call_${calls.length + 1}`,
      tool: parsed.tool,
      args:
        parsed.args && typeof parsed.args === 'object' ? parsed.args : {},
    });
  }
  return calls;
}

/**
 * Execute one tool call. Returns whatever the app-function handler
 * returned (parsed JSON). Throws on transport / handler failure;
 * caller wraps with try/catch and converts to a `tool_result.error`.
 *
 * Tool registry is hardcoded here — keep it in sync with the system
 * prompt's TOOLS section.
 */
async function runTool(call: ParsedToolCall): Promise<unknown> {
  switch (call.tool) {
    case 'list_my_pumps': {
      // Existing handler — returns {Items: Device[]} (DynamoDB-era envelope).
      return getFn('deviceList');
    }
    case 'get_pump_state': {
      const deviceId = String(call.args.deviceId ?? '').trim();
      if (!deviceId) throw new Error('deviceId required');
      // deviceInfo handler reads ctx.request.params.deviceId — must use the
      // alias path, not the canonical /fn/<name> route (which doesn't accept
      // trailing path segments).
      return getFnViaAlias(`/devices/device-info/${encodeURIComponent(deviceId)}`);
    }
    case 'query_pump_history': {
      const args = call.args as Record<string, string | number | undefined>;
      const params: Record<string, string | number | undefined> = {};
      for (const [k, v] of Object.entries(args)) {
        if (v !== undefined && v !== null) params[k] = v;
      }
      return getFn('aquastarQueryPumpHistory', params);
    }
    default:
      throw new Error(`Unknown tool: ${call.tool}`);
  }
}

/**
 * Parse `confirm_action` fences from an assistant response. Same
 * pattern as parseToolCalls but for proposed mutations that need
 * user approval before they run.
 */
function parseConfirmActions(assistantText: string): PendingAction[] {
  const actions: PendingAction[] = [];
  CONFIRM_ACTION_FENCE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = CONFIRM_ACTION_FENCE_RE.exec(assistantText)) !== null) {
    const json = match[1].trim();
    let parsed: any;
    try { parsed = JSON.parse(json); } catch { continue; }
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof parsed.action !== 'string'
    ) {
      continue;
    }
    actions.push({
      id: typeof parsed.id === 'string' ? parsed.id : `act_${actions.length + 1}`,
      action: parsed.action,
      args: parsed.args && typeof parsed.args === 'object' ? parsed.args : {},
      summary:
        typeof parsed.summary === 'string' && parsed.summary.length > 0
          ? parsed.summary
          : `${parsed.action} (no summary provided)`,
    });
  }
  return actions;
}

/**
 * Execute a confirmed pump mutation. Maps action names to existing
 * Web/User handlers (the ones PumpDetail.tsx already calls). Throws
 * on transport / handler failure; caller wraps with try/catch and
 * converts to a `tool_result.error`.
 *
 * v0 actions:
 *   - toggle_pump_power  → PUT /devices/toggle
 *   - select_pump_speed  → PUT /devices/select-current-speed
 *   - set_preset_rpm     → PUT /devices/speed-setting (one slot)
 *   - toggle_schedule    → read deviceInfo, mutate one slot's
 *                          isEnabled, PUT /devices/device-schedule
 *                          with the full array
 *   - set_schedule       → same as toggle_schedule but mutates all
 *                          four slot fields (enabled/start/stop/preset)
 *
 * The toggle/set-schedule actions read the current full schedule
 * array from the server before they mutate, because the underlying
 * `deviceSchedule` handler takes the full array as input (per-item
 * MERGE on the server side). We can't reliably trust the snapshot
 * the LLM has — the user might have edited a different slot from
 * iOS in between turns.
 */
async function runMutation(action: PendingAction): Promise<unknown> {
  switch (action.action) {
    case 'toggle_pump_power': {
      const deviceId = String(action.args.deviceId ?? '').trim();
      const powerOn = Boolean(action.args.powerOn);
      if (!deviceId) throw new Error('deviceId required');
      return fnViaAlias('PUT', '/devices/toggle', {
        deviceId,
        status: powerOn ? 'ON' : 'OFF',
      });
    }
    case 'select_pump_speed': {
      const deviceId = String(action.args.deviceId ?? '').trim();
      const speedName = String(action.args.speedName ?? '').trim();
      if (!deviceId) throw new Error('deviceId required');
      if (!/^S[1-4]$/.test(speedName)) {
        throw new Error(`speedName must be S1, S2, S3, or S4 (got ${speedName})`);
      }
      return fnViaAlias('PUT', '/devices/select-current-speed', {
        deviceId,
        speedName,
      });
    }
    case 'set_preset_rpm': {
      const deviceId = String(action.args.deviceId ?? '').trim();
      const slot = String(action.args.slot ?? '').trim();
      const rpm = Number(action.args.rpm);
      if (!deviceId) throw new Error('deviceId required');
      if (!/^S[1-4]$/.test(slot)) {
        throw new Error(`slot must be S1, S2, S3, or S4 (got ${slot})`);
      }
      if (!Number.isFinite(rpm) || rpm < 800 || rpm > 3600) {
        throw new Error(
          `rpm must be a number between 800 and 3,600 (got ${action.args.rpm})`,
        );
      }
      return fnViaAlias('PUT', '/devices/speed-setting', {
        deviceId,
        speed: { [slot]: Math.round(rpm) },
      });
    }
    case 'toggle_schedule': {
      const deviceId = String(action.args.deviceId ?? '').trim();
      const scheduleId = String(action.args.scheduleId ?? '').trim();
      const enabled = Boolean(action.args.enabled);
      if (!deviceId) throw new Error('deviceId required');
      if (!scheduleId) throw new Error('scheduleId required');
      return mutateScheduleSlot(deviceId, scheduleId, (slot) => ({
        ...slot,
        isEnabled: enabled ? 1 : 0,
      }));
    }
    case 'set_schedule': {
      const deviceId = String(action.args.deviceId ?? '').trim();
      const scheduleId = String(action.args.scheduleId ?? '').trim();
      if (!deviceId) throw new Error('deviceId required');
      if (!scheduleId) throw new Error('scheduleId required');
      const enabled = action.args.enabled !== undefined
        ? Boolean(action.args.enabled)
        : undefined;
      const start = parseHhMm(action.args.start);
      const stop = parseHhMm(action.args.stop);
      const preset = action.args.preset !== undefined
        ? String(action.args.preset).trim()
        : undefined;
      if (preset !== undefined && !/^S[1-4]$/.test(preset)) {
        throw new Error(`preset must be S1, S2, S3, or S4 (got ${preset})`);
      }
      return mutateScheduleSlot(deviceId, scheduleId, (slot) => ({
        ...slot,
        ...(enabled !== undefined ? { isEnabled: enabled ? 1 : 0 } : {}),
        ...(preset ? { SchSpeedSelect: preset } : {}),
        scheduleTime: {
          startTime: start ?? slot.scheduleTime?.startTime ?? { hours: '0', minutes: '0' },
          stopTime: stop ?? slot.scheduleTime?.stopTime ?? { hours: '0', minutes: '0' },
        },
      }));
    }
    default:
      throw new Error(`Unknown action: ${action.action}`);
  }
}

/**
 * Read the device's current schedule array, apply `mutate` to the
 * matching slot (by scheduleId), and PUT the full array back. The
 * `deviceSchedule` handler takes the FULL array — no per-slot endpoint
 * exists — so even a single-slot toggle has to go through this
 * read-modify-write.
 *
 * We always send all three slots (padding with empties if the device
 * returned fewer) to mirror what `ScheduleEditorModal` does and what
 * iOS does. Skipping a slot would orphan it server-side.
 */
async function mutateScheduleSlot(
  deviceId: string,
  scheduleId: string,
  mutate: (slot: ScheduleSlotDraft) => ScheduleSlotDraft,
): Promise<unknown> {
  const devEnvelope = await getFnViaAlias<{ Items: Array<{ deviceSchedules?: ScheduleSlotDraft[] }> } | { deviceSchedules?: ScheduleSlotDraft[] }>(
    `/devices/device-info/${encodeURIComponent(deviceId)}`,
  );
  // deviceInfo returns either { Items: [device] } (envelope) or the
  // device directly depending on alias path. Handle both.
  const dev = (devEnvelope as { Items?: Array<{ deviceSchedules?: ScheduleSlotDraft[] }> }).Items
    ? (devEnvelope as { Items: Array<{ deviceSchedules?: ScheduleSlotDraft[] }> }).Items[0]
    : (devEnvelope as { deviceSchedules?: ScheduleSlotDraft[] });
  const current = dev?.deviceSchedules ?? [];

  // Normalise to 3 slots; pad with disabled defaults at the missing
  // scheduleIds 1..3 if the array is short. Server requires three.
  const byId = new Map<string, ScheduleSlotDraft>();
  for (const s of current) {
    if (s && s.scheduleId) byId.set(String(s.scheduleId), s);
  }
  const slots: ScheduleSlotDraft[] = [];
  for (let i = 1; i <= 3; i++) {
    const sid = String(i);
    const existing = byId.get(sid);
    if (existing) {
      slots.push(sid === scheduleId ? mutate(existing) : existing);
    } else {
      const blank: ScheduleSlotDraft = {
        scheduleId: sid,
        scheduleName: `Schedule ${sid}`,
        isEnabled: 0,
        SchSpeedSelect: 'S1',
        scheduleTime: {
          startTime: { hours: '0', minutes: '0' },
          stopTime: { hours: '0', minutes: '0' },
        },
      };
      slots.push(sid === scheduleId ? mutate(blank) : blank);
    }
  }

  return fnViaAlias('PUT', '/devices/device-schedule', {
    deviceId,
    scheduleDataArray: slots,
  });
}

interface ScheduleSlotDraft {
  scheduleId: string;
  scheduleName?: string;
  isEnabled?: 0 | 1;
  SchSpeedSelect?: string;
  scheduleTime?: {
    startTime?: { hours?: string; minutes?: string };
    stopTime?: { hours?: string; minutes?: string };
  };
}

/** Parse "HH:MM" → {hours, minutes} string pair. Returns null on bad input. */
function parseHhMm(value: unknown): { hours: string; minutes: string } | null {
  if (typeof value !== 'string') return null;
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isInteger(h) || h < 0 || h > 23) return null;
  if (!Number.isInteger(min) || min < 0 || min > 59) return null;
  return { hours: String(h), minutes: String(min) };
}

/**
 * Render a batch of ToolResults as a block of fenced `tool_result`
 * code blocks suitable for shipping back to the model as a synthetic
 * user message. Each result gets its own fence so the model can map
 * them by `id` to its earlier tool calls.
 */
function renderToolResults(results: ToolResult[]): string {
  const fences = results.map((r) => {
    const payload: Record<string, unknown> = { id: r.id, ok: r.ok };
    if (r.ok) payload.data = r.data ?? null;
    else payload.error = r.error ?? 'unknown error';
    return '```tool_result\n' + JSON.stringify(payload) + '\n```';
  });
  return fences.join('\n\n');
}

/** crypto.randomUUID() with a graceful fallback for older browsers. */
function cryptoRandomUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC4122-ish v4 fallback.
  const r = (n: number) => Math.floor(Math.random() * n);
  const hex = Array.from({ length: 16 }, () => r(256).toString(16).padStart(2, '0'));
  hex[6] = ((parseInt(hex[6], 16) & 0x0f) | 0x40).toString(16).padStart(2, '0');
  hex[8] = ((parseInt(hex[8], 16) & 0x3f) | 0x80).toString(16).padStart(2, '0');
  return (
    hex.slice(0, 4).join('') +
    '-' +
    hex.slice(4, 6).join('') +
    '-' +
    hex.slice(6, 8).join('') +
    '-' +
    hex.slice(8, 10).join('') +
    '-' +
    hex.slice(10, 16).join('')
  );
}
